import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

/**
 * Counts in-flight requests per service method so the Class Management v2
 * template can show a skeleton for each list while its data loads — without
 * touching the inherited component code that issues the requests.
 */
@Injectable()
export class ClassLoadTracker {
  private counts = new Map<string, number>();

  inc(key: string): void {
    this.counts.set(key, (this.counts.get(key) || 0) + 1);
  }

  dec(key: string): void {
    this.counts.set(key, Math.max(0, (this.counts.get(key) || 0) - 1));
  }

  isLoading(keys: string[]): boolean {
    return keys.some((k) => (this.counts.get(k) || 0) > 0);
  }
}

/**
 * Wraps a service so every method that returns an Observable reports
 * start/finish (complete, error or unsubscribe) to the tracker. Everything
 * else (fields, non-Observable methods) passes straight through.
 */
export function trackService<T extends object>(service: T, tracker: ClassLoadTracker): T {
  return new Proxy(service, {
    get(target, prop, receiver) {
      const value = Reflect.get(target, prop, receiver);
      if (typeof value !== 'function' || typeof prop !== 'string') {
        return value;
      }
      return (...args: any[]) => {
        const result = value.apply(target, args);
        if (!result || typeof result.subscribe !== 'function') {
          return result;
        }
        return new Observable((subscriber) => {
          tracker.inc(prop);
          const subscription = result.subscribe(subscriber);
          return () => {
            subscription.unsubscribe();
            tracker.dec(prop);
          };
        });
      };
    },
  });
}
